import { Avatar, AvatarProps } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { User } from '@/types';

// Persists for the lifetime of the JS session (across all Inertia navigations).
// Once a URL has loaded, subsequent mounts skip Chakra's fallback entirely.
const loadedUrls = new Set<string>();

type UserAvatarProps = Omit<AvatarProps, 'src' | 'name'> & {
    user: Pick<User, 'name' | 'avatar_url'>;
    href?: string;
};

export function UserAvatar({ user, ...props }: UserAvatarProps) {
    const src = user.avatar_url ?? undefined;

    // Lazy initialiser so remounts after first load start ready immediately.
    const [ready, setReady] = useState(() => !!src && loadedUrls.has(src));

    useEffect(() => {
        if (!src || ready) return;

        const img = new Image();
        img.onload = () => {
            loadedUrls.add(src);
            setReady(true);
        };
        img.src = src;

        return () => {
            img.onload = null;
        };
    }, [src]);

    // ignoreFallback bypasses Chakra's internal useImage loading state,
    // rendering the <img> directly once we know it's in the browser cache.
    return <Avatar name={user.name} src={ready ? src : undefined} ignoreFallback={ready} {...props} />;
}
